import type { ReactNode } from 'react'

import { compact } from '@/lib/format'

/**
 * A stat tile is the right form when the story is one number - no chart needed.
 * The optional sparkline is de-emphasised; the value is the point.
 */
export function StatTile({
  label,
  value,
  hint,
  trend,
  emphasis,
  footer,
}: {
  label: string
  value: number | string
  hint?: string
  trend?: number[]
  emphasis?: boolean
  footer?: ReactNode
}) {
  const display = typeof value === 'number' ? compact(value) : value

  return (
    <div className="card px-4 py-3.5">
      <p className="text-xs font-medium text-ink-muted">{label}</p>
      <p
        className={
          emphasis
            ? 'mt-1.5 text-hero font-semibold text-ink'
            : 'mt-1.5 text-2xl font-semibold text-ink'
        }
      >
        {display}
      </p>
      {hint && <p className="mt-1 text-xs text-ink-faint">{hint}</p>}
      {trend && trend.length > 1 && <Sparkline values={trend} />}
      {footer && <div className="mt-2">{footer}</div>}
    </div>
  )
}

function Sparkline({ values }: { values: number[] }) {
  const width = 120
  const height = 26
  const max = Math.max(...values, 1)
  const step = width / Math.max(values.length - 1, 1)

  const points = values.map((value, index) => ({
    x: index * step,
    y: height - (value / max) * (height - 3) - 1.5,
  }))
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
  const last = points[points.length - 1]

  return (
    <svg
      className="mt-2.5 block"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={`Trend, latest value ${values[values.length - 1]}`}
    >
      <path
        d={path.join(' ')}
        fill="none"
        stroke="var(--series-1)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.55"
      />
      {/* The current period gets the accent; the history recedes. */}
      <circle cx={last.x} cy={last.y} r="3" fill="var(--series-1)"
              stroke="var(--surface-1)" strokeWidth="2" />
    </svg>
  )
}
